import { useCallback, useEffect, useState } from "react";

import type { AutoSuggestedWork } from "@/api/queries";

import { useGetMyWorkAutoSuggest } from "@/api/queries";

import type { WorkSearchOption } from "../types/setlist.types";

export default function useWorkSearch(selectedAccount: string) {
  const [searchString, setSearchString] = useState<string>("");
  const [options, setOptions] = useState<WorkSearchOption[]>([]);
  const [cachedSongs, setCachedSongs] = useState<AutoSuggestedWork[]>([]);
  const [noOptionsMessage, setNoOptionsMessage] = useState<string>("Start typing to search...");
  const works = useGetMyWorkAutoSuggest({
    aac: selectedAccount,
    searchstring: searchString,
  });

  const handleInputChange = useCallback((inputValue: string): void => {
    setSearchString(inputValue);
  }, []);

  useEffect(() => {
    if (Array.isArray(works?.data?.works)) {
      const newSongsFromWorks = works.data.works;

      const newFetchedOptions = newSongsFromWorks.map(songData => ({
        label: `${songData.title} - (${songData.performingartists})`,
        value: songData.workcode,
      }));

      // Conditionally update options to prevent loops
      setOptions((currentOptions) => {
        // Create signatures for comparison. Sorting helps if order isn't guaranteed.
        const currentSig = JSON.stringify(currentOptions.map(o => ({ l: o.label, v: o.value })).sort((a, b) => String(a.v).localeCompare(String(b.v))));
        const newSig = JSON.stringify(newFetchedOptions.map(o => ({ l: o.label, v: o.value })).sort((a, b) => String(a.v).localeCompare(String(b.v))));

        if (currentSig !== newSig) {
          return newFetchedOptions; // Update if different
        }
        return currentOptions; // Return same reference if no actual change
      });

      // Conditionally update cachedSongs
      setCachedSongs((prevCachedSongs) => {
        const existingWorkcodes = new Set(prevCachedSongs.map(s => s.workcode));
        const songsToAdd = newSongsFromWorks.filter(
          (newSong): boolean => !!(newSong.workcode && !existingWorkcodes.has(newSong.workcode)),
        );

        if (songsToAdd.length > 0) {
          // Ensure we don't add duplicates if newSongsFromWorks itself has them
          const uniqueSongsToAdd = songsToAdd.filter((song, index, self) =>
            index === self.findIndex(s => s.workcode === song.workcode),
          );
          if (uniqueSongsToAdd.length > 0) {
            return [...prevCachedSongs, ...uniqueSongsToAdd];
          }
        }
        return prevCachedSongs; // Return same reference if no actual change
      });

      setNoOptionsMessage(
        newFetchedOptions.length === 0
          ? "No options found"
          : "Start typing to search...",
      );
    }
    else if (works.isLoading === false && works.isFetching === false && (!works.data || !works.data.works)) {
      // Handle case where 'works' has loaded/fetched but contains no items or unexpected structure
      setOptions((currentOptions) => {
        if (currentOptions.length > 0)
          return []; // Clear options if they had content
        return currentOptions; // Otherwise, no change if already empty
      });
      setNoOptionsMessage("No options found");
      // Optionally, clear cachedSongs if appropriate for this case
      // setCachedSongs(prevCached => prevCached.length > 0 ? [] : prevCached);
    }
  }, [works.data, works.isLoading, works.isFetching]); // Depend on stable primitives, not the whole query object reference

  return { options, cachedSongs, noOptionsMessage, handleInputChange };
}
